import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tab,
  Tabs,
  Button,
  Divider,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChatIcon from '@mui/icons-material/Chat';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';
import BankerStatusUpdate from '../components/banker/BankerStatusUpdate';
import SanctionLetterUpload from '../components/banker/SanctionLetterUpload';
import DocumentCategoryGrid from '../components/documents/DocumentCategoryGrid';
import CibilReportsSection from '../components/students/CibilReportsSection';
import ChatBox from '../components/chat/ChatBox';
import { getStudentById } from '../services/studentService';
import { getUsersByRole } from '../services/userService';
import {
  ROLE_LABELS,
  ROLE_COLORS,
} from '../config/constants';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 2 }}>
    {value === index && children}
  </Box>
);

// ─── Overview helpers ─────────────────────────────────────────────────────────
const has = (v) => v != null && v !== '';

const InfoField = ({ label, value }) => (
  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' } }}>
    <TableCell sx={{ width: 200, py: 1.25, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
      {label}
    </TableCell>
    <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontSize: '0.85rem', fontWeight: 500, color: has(value) ? '#1E293B' : '#94A3B8', fontStyle: has(value) ? 'normal' : 'italic' }}>
      {has(value) ? String(value) : '—'}
    </TableCell>
  </TableRow>
);

const InfoFieldRow = ({ fields }) => (
  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' } }}>
    {fields.map(({ label, value }, i) => (
      <React.Fragment key={i}>
        <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
          {label}
        </TableCell>
        <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontSize: '0.85rem', fontWeight: 500, color: has(value) ? '#1E293B' : '#94A3B8', fontStyle: has(value) ? 'normal' : 'italic' }}>
          {has(value) ? String(value) : '—'}
        </TableCell>
      </React.Fragment>
    ))}
  </TableRow>
);

const SectionHeader = ({ title, icon, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, backgroundColor: color, borderRadius: '8px 8px 0 0' }}>
    <Box sx={{ color: '#fff', display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{title}</Typography>
  </Box>
);

// ─── Loan Form PDF Download ──────────────────────────────────────────────────
const downloadLoanFormPDF = (student, applicationForm) => {
  if (!applicationForm) return;
  const { s1 = {}, s2 = {}, s3 = {}, s4 = {}, s5 = {} } = applicationForm;

  const esc = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const row = (label, value) => {
    const v = has(value) ? esc(value) : '<span class="empty">Not provided</span>';
    return `<tr><td class="label">${esc(label)}</td><td>${v}</td></tr>`;
  };
  const row2 = (l1, v1, l2, v2) => {
    const a = has(v1) ? esc(v1) : '<span class="empty">Not provided</span>';
    const b = has(v2) ? esc(v2) : '<span class="empty">Not provided</span>';
    return `<tr><td class="label">${esc(l1)}</td><td>${a}</td><td class="label">${esc(l2)}</td><td>${b}</td></tr>`;
  };
  const sh = (num, title, cls) => `<div class="section-header ${cls}">Section ${num} — ${title}</div>`;
  const sub = (label) => `<div class="sub-head">${label}</div>`;

  const title = `Loan Application - ${esc(student?.name || 'Student')}${student?.fileNumber ? ` (${esc(student.fileNumber)})` : ''}`;
  const now = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const html = `<!DOCTYPE html><html><head><title>${title}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif; color:#1E293B; padding:28px 32px; font-size:12.5px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
h1 { font-size:20px; font-weight:700; color:#1E3A5F; margin-bottom:2px; }
.meta { font-size:11px; color:#5F6368; margin-bottom:20px; }
.section-header { color:#fff; padding:9px 16px; font-weight:700; font-size:13px; border-radius:6px 6px 0 0; margin-top:18px; }
.section-header.blue { background:#2C7BE5; } .section-header.purple { background:#7C3AED; } .section-header.green { background:#059669; } .section-header.amber { background:#D97706; } .section-header.gray { background:#475569; }
.sub-head { background:#F1F5F9; padding:5px 16px; font-weight:700; font-size:11px; color:#475569; text-transform:uppercase; letter-spacing:0.5px; border:1px solid #E2E8F0; border-top:none; }
table { width:100%; border-collapse:collapse; }
td { padding:7px 16px; border:1px solid #E2E8F0; font-size:12.5px; vertical-align:top; }
td.label { font-weight:600; color:#475569; white-space:nowrap; width:22%; }
tr:nth-child(odd) td { background:#F8FAFC; }
.empty { color:#94A3B8; font-style:italic; }
@media print { body { padding:16px 20px; } @page { margin:12mm; } }
</style></head><body>
<h1>${title}</h1><div class="meta">Generated on ${now}</div>
${sh(1, 'Student Details', 'blue')}
${sub('1.1 Basic Information')}<table>${row2('Full Name', student?.name, 'Email', student?.email)}${row2('Phone', student?.phone, 'Marital Status', s1.maritalStatus)}</table>
${sub('1.2 Address Information')}<table>${row('Current Address', s1.currentAddress)}${row('Years at Current Address', s1.currentAddressYears)}${row('Permanent Address', s1.permanentAddress)}</table>
${sub('1.3 Academic Information')}<table>${row2('10th Score (%)', s1.tenthScore, '12th Score (%)', s1.twelfthScore)}${row2("Bachelor's Score (%)", s1.bachelorsScore, 'Graduation Backlogs', s1.graduationBacklogs)}</table>
${sub('1.4 Test Scores')}<table>${row2('Language Test + Score', s1.languageTest, 'GRE / GMAT Score', s1.greGmatScore)}</table>
${sub('1.5 Reference Details')}<table>${row2('Reference Mobile', s1.refPhone, 'Reference Address', s1.refAddress)}</table>
${sub('1.6 Work Profile')}<table>${row2('Employment Type', s1.employmentType, 'Experience (Years)', s1.workExperience)}${row('Company Name & Address', s1.companyAddress)}</table>
${sh(2, 'University Details', 'purple')}<table>${row2('Target Country', s2.targetCountry, 'University Name', s2.universityName)}${row2('Course Name', s2.courseName, 'Course Duration', s2.courseDuration)}${row('Loan Amount Required', s2.loanAmount ? '₹' + s2.loanAmount + ' Lakhs' : null)}</table>
${sh(3, 'Parents Details', 'green')}${sub("Father's Details")}<table>${row2("Father's Name", s3.fatherName, "Father's Contact", s3.fatherPhone)}${row("Father's Email", s3.fatherEmail)}</table>${sub("Mother's Details")}<table>${row2("Mother's Name", s3.motherName, "Mother's Contact", s3.motherPhone)}${row("Mother's Email", s3.motherEmail)}</table>${sub('Residence Address')}<table>${row('Residence Address', s3.residenceAddress)}</table>
${sh(4, 'Co-Applicant Details', 'amber')}${sub('4.1 Basic Details')}<table>${row2('Name', s4.name, 'Contact', s4.phone)}${row2('Email', s4.email, 'Relationship', s4.relationship)}${row('Address', s4.address)}</table>${sub('4.2 Employment')}<table>${row2('Occupation', s4.occupation, 'Years in Job', s4.yearsInJob)}${row('Office Address', s4.officeAddress)}${row2('Annual Income (ITR)', s4.annualIncome ? '₹' + s4.annualIncome + ' L' : null, 'Monthly EMIs', s4.monthlyEmis ? '₹' + s4.monthlyEmis : null)}</table>${sub('4.3 Family')}<table>${row2("Father's Name", s4.fatherName, "Mother's Name", s4.motherName)}</table>${sub('4.4 References')}<table>${row2('Ref 1 Name', s4.ref1Name, 'Ref 1 Mobile', s4.ref1Phone)}${row2('Ref 1 Email', s4.ref1Email, 'Ref 1 Address', s4.ref1Address)}${row2('Ref 2 Name', s4.ref2Name, 'Ref 2 Mobile', s4.ref2Phone)}${row2('Ref 2 Email', s4.ref2Email, 'Ref 2 Address', s4.ref2Address)}</table>
${sh(5, 'Miscellaneous', 'gray')}<table>${row('Previously Applied Banks', s5.previousBanks)}${row2('Own House?', s5.ownHouse, 'Collateral?', s5.collateral)}${s5.collateralTypes?.length ? row('Collateral Types', s5.collateralTypes.join(', ')) : ''}</table>
<script>setTimeout(function(){ window.print(); },300);</script></body></html>`;

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
};

// ─── Loan Form View (inline, read-only table) ────────────────────────────────
const LoanFormSubHead = ({ label }) => (
  <Box sx={{ backgroundColor: '#F1F5F9', px: 2, py: 0.75, border: '1px solid #E2E8F0' }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
  </Box>
);

const LoanFormTab = ({ applicationForm, student }) => {
  if (!applicationForm) {
    return (
      <Alert severity="info" icon={<PersonIcon />} sx={{ borderRadius: 2 }}>
        <Typography fontWeight={600} fontSize="0.875rem">Loan Application Not Submitted</Typography>
        <Typography fontSize="0.8rem" sx={{ mt: 0.25, color: 'text.secondary' }}>
          The student hasn't filled their loan application form yet.
        </Typography>
      </Alert>
    );
  }

  const { s1 = {}, s2 = {}, s3 = {}, s4 = {}, s5 = {}, status, submittedAt } = applicationForm;
  const isSubmitted = status === 'submitted';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip label={isSubmitted ? 'Submitted' : 'Draft'} color={isSubmitted ? 'success' : 'warning'} size="small" sx={{ fontWeight: 700 }} />
          {isSubmitted && submittedAt && <Typography variant="caption" color="text.secondary">Submitted on {fmtDate(submittedAt)}</Typography>}
        </Box>
        <Button variant="contained" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => downloadLoanFormPDF(student, applicationForm)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Download PDF
        </Button>
      </Box>

      {/* Section 1 */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader title="Section 1 — Student Details" icon={<PersonIcon />} color="#2C7BE5" />
        <LoanFormSubHead label="1.1 Basic Information" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Full Name', value: student?.name }, { label: 'Email', value: student?.email }]} />
          <InfoFieldRow fields={[{ label: 'Phone', value: student?.phone }, { label: 'Marital Status', value: s1.maritalStatus }]} />
        </TableBody></Table>
        <LoanFormSubHead label="1.2 Address Information" />
        <Table><TableBody>
          <InfoField label="Current Address (with Pin Code)" value={s1.currentAddress} />
          <InfoField label="Years at Current Address" value={s1.currentAddressYears} />
          <InfoField label="Permanent Address (with Pin Code)" value={s1.permanentAddress} />
        </TableBody></Table>
        <LoanFormSubHead label="1.3 Academic Information" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: '10th Score (%)', value: s1.tenthScore }, { label: '12th Score (%)', value: s1.twelfthScore }]} />
          <InfoFieldRow fields={[{ label: "Bachelor's Score (%)", value: s1.bachelorsScore }, { label: 'Graduation Backlogs', value: s1.graduationBacklogs }]} />
        </TableBody></Table>
        <LoanFormSubHead label="1.4 Test Scores" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Language Test + Score', value: s1.languageTest }, { label: 'GRE / GMAT Score', value: s1.greGmatScore }]} />
        </TableBody></Table>
        <LoanFormSubHead label="1.5 Reference Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Reference Mobile', value: s1.refPhone }, { label: 'Reference Address', value: s1.refAddress }]} />
        </TableBody></Table>
        <LoanFormSubHead label="1.6 Work Profile" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Employment Type', value: s1.employmentType }, { label: 'Experience (Years)', value: s1.workExperience }]} />
          <InfoField label="Company Name & Address" value={s1.companyAddress} />
        </TableBody></Table>
      </Paper>

      {/* Section 2 */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader title="Section 2 — University Details" icon={<SchoolIcon />} color="#7C3AED" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Target Country', value: s2.targetCountry }, { label: 'University Name', value: s2.universityName }]} />
          <InfoFieldRow fields={[{ label: 'Course Name', value: s2.courseName }, { label: 'Course Duration', value: s2.courseDuration }]} />
          <InfoField label="Loan Amount Required" value={s2.loanAmount ? `₹${s2.loanAmount} Lakhs` : null} />
        </TableBody></Table>
      </Paper>

      {/* Section 3 */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader title="Section 3 — Parents Details" icon={<PersonIcon />} color="#059669" />
        <LoanFormSubHead label="Father's Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: "Father's Name", value: s3.fatherName }, { label: "Father's Contact", value: s3.fatherPhone }]} />
          <InfoField label="Father's Email" value={s3.fatherEmail} />
        </TableBody></Table>
        <LoanFormSubHead label="Mother's Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: "Mother's Name", value: s3.motherName }, { label: "Mother's Contact", value: s3.motherPhone }]} />
          <InfoField label="Mother's Email" value={s3.motherEmail} />
        </TableBody></Table>
        <LoanFormSubHead label="Residence Address" />
        <Table><TableBody>
          <InfoField label="Residence Address (with Pin Code)" value={s3.residenceAddress} />
        </TableBody></Table>
      </Paper>

      {/* Section 4 */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader title="Section 4 — Co-Applicant Details" icon={<AccountBalanceIcon />} color="#D97706" />
        <LoanFormSubHead label="4.1 Basic Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Co-Applicant Name', value: s4.name }, { label: 'Contact Number', value: s4.phone }]} />
          <InfoFieldRow fields={[{ label: 'Email ID', value: s4.email }, { label: 'Relationship', value: s4.relationship }]} />
          <InfoField label="Address (with Pin Code)" value={s4.address} />
        </TableBody></Table>
        <LoanFormSubHead label="4.2 Employment Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Occupation', value: s4.occupation }, { label: 'Years in Current Job', value: s4.yearsInJob }]} />
          <InfoField label="Office Name & Address" value={s4.officeAddress} />
          <InfoFieldRow fields={[{ label: 'Annual Income (ITR)', value: s4.annualIncome ? `₹${s4.annualIncome} L` : null }, { label: 'Monthly EMIs', value: s4.monthlyEmis ? `₹${s4.monthlyEmis}` : null }]} />
        </TableBody></Table>
        <LoanFormSubHead label="4.3 Family Details" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: "Father's Name", value: s4.fatherName }, { label: "Mother's Name", value: s4.motherName }]} />
        </TableBody></Table>
        <LoanFormSubHead label="4.4 References" />
        <Table><TableBody>
          <InfoFieldRow fields={[{ label: 'Ref 1 — Name', value: s4.ref1Name }, { label: 'Ref 1 — Mobile', value: s4.ref1Phone }]} />
          <InfoFieldRow fields={[{ label: 'Ref 1 — Email', value: s4.ref1Email }, { label: 'Ref 1 — Address', value: s4.ref1Address }]} />
          <InfoFieldRow fields={[{ label: 'Ref 2 — Name', value: s4.ref2Name }, { label: 'Ref 2 — Mobile', value: s4.ref2Phone }]} />
          <InfoFieldRow fields={[{ label: 'Ref 2 — Email', value: s4.ref2Email }, { label: 'Ref 2 — Address', value: s4.ref2Address }]} />
        </TableBody></Table>
        <LoanFormSubHead label="4.5 Additional Co-Applicant" />
        <Table><TableBody>
          <InfoField label="Has Additional Co-Applicant?" value={s4.hasAdditional} />
        </TableBody></Table>
      </Paper>

      {/* Section 5 */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader title="Section 5 — Miscellaneous" icon={<PersonIcon />} color="#475569" />
        <Table><TableBody>
          <InfoField label="Previously Applied Banks" value={s5.previousBanks} />
          <InfoFieldRow fields={[{ label: 'Own House Available?', value: s5.ownHouse }, { label: 'Ready to Provide Collateral?', value: s5.collateral }]} />
          {s5.collateralTypes?.length > 0 && <InfoField label="Accepted Collateral Types" value={s5.collateralTypes.join(', ')} />}
        </TableBody></Table>
      </Paper>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BankerFileDetailPage = () => {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [applicationForm, setApplicationForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const chatInputRef = useRef(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const studentRes = await getStudentById(id);
      setStudent(studentRes.data.data?.student || null);
      setApplicationForm(studentRes.data.data?.applicationForm || null);
    } catch (err) {
      console.error('Failed to fetch student details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  // Build team members list for chat @mentions
  useEffect(() => {
    if (!student) return;
    const members = [];
    if (student.managerId?.name) {
      members.push({ name: student.managerId.name, role: 'manager', id: student.managerId._id });
    }
    if (student.associateId?.name) {
      members.push({ name: student.associateId.name, role: 'associate', id: student.associateId._id });
    }
    // Fetch admin users
    getUsersByRole('admin')
      .then(({ data }) => {
        const admins = data.data?.users || data.data || [];
        admins.forEach((admin) => {
          if (admin.name) {
            members.push({ name: admin.name, role: 'admin', id: admin._id });
          }
        });
        setTeamMembers([...members]);
      })
      .catch(() => {
        // Even if admin fetch fails, still set manager/associate
        setTeamMembers([...members]);
      });
  }, [student]);

  const handleMentionClick = (member) => {
    chatInputRef.current?.insertMention(member);
  };

  const handleStatusUpdate = (newStatus) => {
    setStudent((prev) => (prev ? { ...prev, status: newStatus } : prev));
  };

  const handleSanctionUpload = (url) => {
    setStudent((prev) => prev ? { ...prev, sanctionLetterUrl: url } : prev);
  };


  if (loading) return <LoadingSpinner fullPage />;
  if (!student) {
    return (
      <Box>
        <PageHeader title="File Not Found" />
        <Typography color="text.secondary">The requested student file was not found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={student.name || 'Student File'}
        breadcrumbs={[
          { label: 'Banker Portal', link: '/banker-portal' },
          { label: student.fileNumber || 'File Detail' },
        ]}
      >
        <StatusBadge status={student.status} />
      </PageHeader>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Documents" />
          <Tab label="CIBIL Reports" />
          <Tab label="Loan Form" />
          <Tab label="Chat" icon={<ChatIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Status Update" />
        </Tabs>

        <Box sx={{ px: 3 }}>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            {/* Student Info */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
              <SectionHeader title="Student Information" icon={<PersonIcon />} color="#2C7BE5" />
              <Table><TableBody>
                <InfoFieldRow fields={[{ label: 'File Number', value: student.fileNumber }, { label: 'Name', value: student.name }]} />
                <InfoFieldRow fields={[{ label: 'Email', value: student.email }, { label: 'Phone', value: student.phone }]} />
                <InfoFieldRow fields={[{ label: 'Date of Birth', value: student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString('en-IN') : null }, { label: 'Status', value: student.status }]} />
              </TableBody></Table>
            </Paper>

            {/* Course & Loan Info */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
              <SectionHeader title="Course & Loan Details" icon={<SchoolIcon />} color="#7C3AED" />
              <Table><TableBody>
                <InfoFieldRow fields={[{ label: 'Course', value: student.course }, { label: 'University', value: student.university }]} />
                <InfoFieldRow fields={[{ label: 'Country', value: student.country }, { label: 'Intake', value: student.intakeMonth ? `${student.intakeMonth} ${student.intakeYear || ''}` : null }]} />
                <InfoFieldRow fields={[{ label: 'Loan Amount', value: student.loanAmount ? `₹${Number(student.loanAmount).toLocaleString('en-IN')}` : null }, { label: 'Assigned Bank', value: student.assignedBank }]} />
                <InfoFieldRow fields={[{ label: 'Sanctioned Amount', value: student.sanctionedAmount ? `₹${Number(student.sanctionedAmount).toLocaleString('en-IN')}` : null }, { label: 'Disbursed Amount', value: student.disbursedAmount ? `₹${Number(student.disbursedAmount).toLocaleString('en-IN')}` : null }]} />
              </TableBody></Table>
            </Paper>

            {/* Assigned Team */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
              <SectionHeader title="Assigned Team" icon={<PersonIcon />} color="#059669" />
              <Table><TableBody>
                <InfoFieldRow fields={[{ label: 'Manager', value: student.managerId?.name }, { label: 'Manager Email', value: student.managerId?.email }]} />
                <InfoFieldRow fields={[{ label: 'Associate', value: student.associateId?.name }, { label: 'Associate Email', value: student.associateId?.email }]} />
                <InfoFieldRow fields={[{ label: 'Banker', value: student.bankerId?.name }, { label: 'Bank Name', value: student.bankerId?.bankName }]} />
              </TableBody></Table>
            </Paper>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={tabValue} index={1}>
            <DocumentCategoryGrid studentId={id} />
            <Divider sx={{ my: 3 }} />
            <SanctionLetterUpload studentId={id} currentUrl={student.sanctionLetterUrl} onUpload={handleSanctionUpload} />
          </TabPanel>

          {/* CIBIL Reports Tab */}
          <TabPanel value={tabValue} index={2}>
            <CibilReportsSection studentId={id} student={student} onRefresh={fetchData} />
          </TabPanel>

          {/* Loan Form Tab */}
          <TabPanel value={tabValue} index={3}>
            <LoanFormTab applicationForm={applicationForm} student={student} />
          </TabPanel>

          {/* Chat Tab */}
          <TabPanel value={tabValue} index={4}>
            {/* Team Members Bar */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                borderRadius: 2,
                backgroundColor: '#FAFBFC',
              }}
            >
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
                Team:
              </Typography>
              {teamMembers.map((member) => (
                <Chip
                  key={member.id || member.name}
                  label={`${member.name}`}
                  size="small"
                  onClick={() => handleMentionClick(member)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    backgroundColor: ROLE_COLORS[member.role] || '#9e9e9e',
                    color: '#fff',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                    '& .MuiChip-label': { px: 1 },
                  }}
                  title={`${ROLE_LABELS[member.role] || member.role} — Click to @mention`}
                />
              ))}
              {teamMembers.length === 0 && (
                <Typography variant="caption" color="text.disabled">No team members assigned</Typography>
              )}
            </Paper>

            {/* Chat Box */}
            <Box sx={{ height: 500, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <ChatBox studentId={id} teamMembers={teamMembers} chatInputRef={chatInputRef} />
            </Box>
          </TabPanel>

          {/* Status Update Tab */}
          <TabPanel value={tabValue} index={5}>
            <BankerStatusUpdate student={student} onStatusUpdate={handleStatusUpdate} />
          </TabPanel>
        </Box>
      </Paper>

    </Box>
  );
};

export default BankerFileDetailPage;
